import React from 'react';

import { get } from '@lumapps/constants';
import { ContentLayoutProps } from '@lumapps/content-layout/components/ContentLayout';
import { isUserDirectory } from '@lumapps/content-types/utils';
import { Redirect, useParams } from '@lumapps/router';
import { createRelativeUrl } from '@lumapps/router/utils';
import { Tutorial } from '@lumapps/tutorials/components/Tutorial';
import { userDirectoryView } from '@lumapps/user-directory-front-office/routes';
import { useHandleNotAllowed } from '@lumapps/utils/hooks/useHandleNotAllowed';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { useContent } from '../../hooks/useContent';
import { useContentStatusNotification } from '../../hooks/useContentStatusNotification';
import { BaseContentPage } from '../BaseContentPage';
import { ContentContextualActions } from '../ContentContextualActions';

const constants = get();

interface ContentPageProps {
    /** List of authorized widgets. */
    enabledWidgetTypes?: ContentLayoutProps['enabledWidgetTypes'];
    /** Page content ref. */
    contentRef?: ContentLayoutProps['contentRef'];
}

export const ContentPage: React.FC<ContentPageProps> = ({ enabledWidgetTypes, contentRef }) => {
    const { id }: any = useParams();
    const {
        currentContentTitle,
        currentContentType,
        pageId,
        isHomePage,
        currentContentSlug: slug,
        canCurrentUserEditContent,
    } = useContent();
    const [status, setStatus] = React.useState<BaseLoadingStatus>(BaseLoadingStatus.loading);

    /**
     * Get the Content ID from 3 different sources, depending on the use case
     * Default - from the param URL
     * Homepage - from the store (set by the front-init)
     * Content with legacy URL - from the initialContent
     * */
    const contentId = id || (isHomePage && pageId) || constants.initialContentId;

    // Handle #not-allowed URL hash on home page
    useHandleNotAllowed(isHomePage);

    useContentStatusNotification({
        contentId,
        currentContentType,
        isNotificationDisplayable: canCurrentUserEditContent && status === BaseLoadingStatus.idle,
    });

    // Redirect to user directory v2
    if (contentId && slug && isUserDirectory(currentContentType)) {
        const redirectURL = createRelativeUrl(userDirectoryView({ contentId, slug }));
        return <Redirect to={redirectURL} push={false} />;
    }

    if (!contentId) {
        return null;
    }

    return (
        <BaseContentPage
            contentId={contentId}
            contentSlug={slug}
            contentTitle={currentContentTitle}
            contentType={currentContentType}
            isHomePage={isHomePage}
            before={status === BaseLoadingStatus.idle && <ContentContextualActions />}
            after={<Tutorial />}
            enabledWidgetTypes={enabledWidgetTypes}
            contentRef={contentRef}
            onStatusChange={setStatus}
        />
    );
};
